import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  fromEvent,
  map,
  of,
  startWith,
} from 'rxjs';

import { onAttributeChange$ } from 'common/utils/attribute-change';

/**
 * Media queries break points for ts logic
 *
 * Together all break points create named intervals
 * This values is break points, it's maximum value for some named interval.
 *
 * For example there is interval 'xs'. Interval created from two break point - 'xxs: 320' and 'xs: 480', so interval is [320, 480)
 *
 * @WARN should be synced with media.scss!
 *
 * xss - [0   , 320)
 * xs  - [320 , 480)
 * sm  - [480 , 640)
 * md  - [640 , 960)
 * lg  - [960 , 1280)
 * xl  - [1280, 1440)
 * xxl - [1440, ∞)
 */
const MEDIA_BREAK_POINTS = {
  ['xxs']: 0,
  ['xs']: 320,
  ['sm']: 480,
  ['md']: 640,
  ['lg']: 960,
  ['xl']: 1280,
  ['xxl']: 1440,
} as const;

const MEDIA_BREAK_POINTS_ARRAY = Object.entries(MEDIA_BREAK_POINTS).sort(
  (a, b) => (a[1] > b[1] ? 1 : -1),
) as [keyof typeof MEDIA_BREAK_POINTS, number][];

export type MediaBreakPoint = keyof typeof MEDIA_BREAK_POINTS;

/**
 * Get breakpoint as min value for media query interval. Null if min value is 0
 *
 * @param name
 */
function breakpointMin(name: MediaBreakPoint | null): number | null {
  if (!name) {
    return null;
  }

  const min = MEDIA_BREAK_POINTS[name];
  return min ? min : null;
}

/**
 * Get breakpoint as max value for media query interval
 *
 * @param name
 */
function breakpointMax(name: MediaBreakPoint | null): number | null {
  if (!name) {
    return null;
  }

  const max = MEDIA_BREAK_POINTS[name];
  return max ? max - 0.02 : null;
}

/**
 * Get next breakpoint name. Null if no next breakpoint
 *
 * @param name
 */
function breakpointNext(name: MediaBreakPoint | null): MediaBreakPoint | null {
  if (!name) {
    return null;
  }

  const index = MEDIA_BREAK_POINTS_ARRAY.findIndex(([bp]) => bp === name);
  return index >= 0 && index < MEDIA_BREAK_POINTS_ARRAY.length
    ? MEDIA_BREAK_POINTS_ARRAY[index + 1][0]
    : null;
}

/**
 * Check for adaptive allowed
 */
function isAdaptiveAllowed(): boolean {
  return document.body.classList.contains('isp-adaptive');
}

/**
 * Stream of adaptive allowance
 */
function isAdaptiveAllowed$(): Observable<boolean> {
  return onAttributeChange$(document.body, true).pipe(
    map(() => isAdaptiveAllowed()),
    distinctUntilChanged(),
  );
}

/**
 * Get media query list for breakpoint, starting from breakpoint
 *
 * @param name - media breakpoint name
 */
function mediaBreakpointUp(name: MediaBreakPoint): MediaQueryList | null {
  const min = breakpointMin(name);
  if (min) {
    const mediaQueryString = `(min-width: ${min}px)`;
    return window.matchMedia(mediaQueryString);
  }
  return null;
}

/**
 * Check that window match media query, starting from breakpoint
 * Example:
 * matchMediaBreakpointUp('md') -> true, for viewport 'width >= 640px'
 *
 * @param name - media breakpoint name
 * @param checkForAdaptiveAllowed - should check allowance for adaptive feature. If not return false for any media match
 */
export function matchMediaBreakpointUp(
  name: MediaBreakPoint,
  checkForAdaptiveAllowed = true,
): boolean {
  if (checkForAdaptiveAllowed && !isAdaptiveAllowed()) {
    return false;
  }

  const mediaQuery = mediaBreakpointUp(name);
  return !mediaQuery ? true : mediaQuery.matches;
}

/**
 * Get stream of media query matches for that window, starting from breakpoint
 * Example:
 * matchMediaBreakpointUp$('md') -> true, for viewport 'width >= 640px'
 *
 * @param name - media breakpoint name
 * @param checkForAdaptiveAllowed - should check allowance for adaptive feature. If not return false for any media match
 */
export function matchMediaBreakpointUp$(
  name: MediaBreakPoint,
  checkForAdaptiveAllowed = true,
): Observable<boolean> {
  const mediaQuery = mediaBreakpointUp(name);
  if (!mediaQuery) {
    return of(true);
  }

  return combineLatest([
    isAdaptiveAllowed$(),
    fromEvent<MediaQueryList>(mediaQuery, 'change').pipe(
      startWith(mediaQuery),
      map((list: MediaQueryList) => list.matches),
    ),
  ]).pipe(
    map(([isAllowed, matched]) => {
      if (checkForAdaptiveAllowed) {
        return !isAllowed ? false : matched;
      }

      return matched;
    }),
    distinctUntilChanged(),
  );
}

/**
 * Get media query list for breakpoint, starting from 0 to breakpoint
 *
 * @param name - media breakpoint name
 */
function mediaBreakpointDown(name: MediaBreakPoint): MediaQueryList | null {
  const max = breakpointMax(name);
  if (max) {
    const mediaQueryString = `(max-width: ${max}px)`;
    return window.matchMedia(mediaQueryString);
  }
  return null;
}

/**
 * Check that window match media query, starting from 0 to breakpoint
 * Example:
 * matchMediaBreakpointDown('md') -> true, for viewport 'width <= 639.8px'
 *
 * @param name - media breakpoint name
 * @param checkForAdaptiveAllowed - should check allowance for adaptive feature. If not return false for any media match
 */
export function matchMediaBreakpointDown(
  name: MediaBreakPoint,
  checkForAdaptiveAllowed = true,
): boolean {
  if (checkForAdaptiveAllowed && !isAdaptiveAllowed()) {
    return false;
  }

  const mediaQuery = mediaBreakpointDown(name);
  return !mediaQuery ? true : mediaQuery.matches;
}

/**
 * Check that window match media query, starting from 0 to breakpoint
 * Example:
 * matchMediaBreakpointDown$('md') -> true, for viewport 'width <= 639.8px'
 *
 * @param name - media breakpoint name
 * @param checkForAdaptiveAllowed - should check allowance for adaptive feature. If not return false for any media match
 */
export function matchMediaBreakpointDown$(
  name: MediaBreakPoint,
  checkForAdaptiveAllowed = true,
): Observable<boolean> {
  const mediaQuery = mediaBreakpointDown(name);
  if (!mediaQuery) {
    return of(true);
  }

  return combineLatest([
    isAdaptiveAllowed$(),
    fromEvent<MediaQueryList>(mediaQuery, 'change').pipe(
      startWith(mediaQuery),
      map((list: MediaQueryList) => list.matches),
    ),
  ]).pipe(
    map(([isAllowed, matched]) => {
      if (checkForAdaptiveAllowed) {
        return !isAllowed ? false : matched;
      }

      return matched;
    }),
    distinctUntilChanged(),
  );
}

/**
 * Get media query list for breakpoint, starting from breakpoint to next breakpoint
 *
 * @param name - media breakpoint name
 */
function mediaBreakpointOnly(name: MediaBreakPoint): MediaQueryList | null {
  const min = breakpointMin(name);
  const next = breakpointNext(name);
  const max = breakpointMax(next);

  if (min && max) {
    const mediaQueryString = `(min-width: ${min}px) and (max-width: ${max}px)`;
    return window.matchMedia(mediaQueryString);
  } else if (!max) {
    return mediaBreakpointUp(name);
  } else if (!min && next) {
    return mediaBreakpointDown(next);
  }

  return null;
}

/**
 * Check that window match media query, starting from breakpoint to next breakpoint
 * Example:
 * matchMediaBreakpointOnly('md') -> true, for viewport '959.8px >= width >= 640px'
 *
 * @param name - media breakpoint name
 * @param checkForAdaptiveAllowed - should check allowance for adaptive feature. If not return false for any media match
 */
export function matchMediaBreakpointOnly(
  name: MediaBreakPoint,
  checkForAdaptiveAllowed = true,
): boolean {
  if (checkForAdaptiveAllowed && !isAdaptiveAllowed()) {
    return false;
  }

  const mediaQuery = mediaBreakpointOnly(name);
  return !mediaQuery ? true : mediaQuery.matches;
}

/**
 * Check that window match media query, starting from breakpoint to next breakpoint
 * Example:
 * matchMediaBreakpointOnly$('md') -> true, for viewport '959.8px >= width >= 640px'
 *
 * @param name - media breakpoint name
 * @param checkForAdaptiveAllowed - should check allowance for adaptive feature. If not return false for any media match
 */
export function matchMediaBreakpointOnly$(
  name: MediaBreakPoint,
  checkForAdaptiveAllowed = true,
): Observable<boolean> {
  const mediaQuery = mediaBreakpointOnly(name);
  if (!mediaQuery) {
    return of(true);
  }

  return combineLatest([
    isAdaptiveAllowed$(),
    fromEvent<MediaQueryList>(mediaQuery, 'change').pipe(
      startWith(mediaQuery),
      map((list: MediaQueryList) => list.matches),
    ),
  ]).pipe(
    map(([isAllowed, matched]) => {
      if (checkForAdaptiveAllowed) {
        return !isAllowed ? false : matched;
      }

      return matched;
    }),
    distinctUntilChanged(),
  );
}
